import type { FontWeightOption } from "@v2/features/modifiers/utils/fontWeightUtils";

import { useMemo } from "react";

import { skipToken } from "@tanstack/react-query";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import {
  findByWeight,
  findClosestWeight,
  getAvailableWeights,
  normalizeFontFamily,
} from "@v2/features/modifiers/utils/fontWeightUtils";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { fontRouter } from "@v2/trpc/routes/font";
import { FONT_WEIGHT_OPTIONS } from "replo-runtime/shared/utils/font";

interface UseFontWeightOptionsResult {
  options: FontWeightOption[];
  displayOption: FontWeightOption | undefined;
  isDisabled: boolean;
}

/**
 * Returns the available font weight options for a given font family, along with
 * the display option to show (handling exact match, closest weight, and fallbacks).
 * @param fontFamily - The font family to get weights for
 * @param selectedWeight - The selected weight to get the display option for
 * @returns The available font weight options, the display option, and whether the options are disabled
 */
export function useFontWeightOptions(
  fontFamily: string | null,
  selectedWeight: number | null,
): UseFontWeightOptionsResult {
  const projectId = useCurrentProjectId();
  const { data: projectFonts = [] } = fontRouter.getFonts.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const optimisticFontFamily = useOptimisticModifierValue({
    modifierType: { type: "fontFamily" as const, value: "" },
    extractor: (elementDesignation) => {
      return elementDesignation.element.styleOverrides?.fontFamily ?? null;
    },
  });

  const resolvedFontFamily = optimisticFontFamily ?? fontFamily;

  const options = useMemo(() => {
    const defaultWeightOptions = FONT_WEIGHT_OPTIONS.map(
      ({ label, value }) => ({
        label,
        value: value.toString(),
      }),
    );

    if (!resolvedFontFamily) {
      return defaultWeightOptions;
    }

    const normalizedFamily = normalizeFontFamily(resolvedFontFamily);
    const availableWeights: number[] = getAvailableWeights(
      normalizedFamily,
      projectFonts,
    );

    const availableWeightsSet = new Set(
      availableWeights.map((w) => w.toString()),
    );

    const filteredOptions = FONT_WEIGHT_OPTIONS.filter((fontWeight) => {
      return availableWeightsSet.has(fontWeight.value.toString());
    }).map(({ label, value }) => ({
      label,
      value: value.toString(),
    }));

    return filteredOptions.length > 0 ? filteredOptions : defaultWeightOptions;
  }, [resolvedFontFamily, projectFonts]);

  const displayOption = useMemo(() => {
    if (selectedWeight != null) {
      const exactFontWeightMatch = findByWeight(options, selectedWeight);
      if (exactFontWeightMatch) {
        return exactFontWeightMatch;
      }
      const closestAvailableWeight = findClosestWeight(
        options.map((opt) => Number(opt.value)),
        selectedWeight,
      );
      return findByWeight(options, closestAvailableWeight);
    }

    return options.length === 1 ? options[0] : undefined;
  }, [options, selectedWeight]);

  const isDisabled = options.length <= 1;

  return { options, displayOption, isDisabled };
}
